/* ==========================================================================
   Hanlyn mobile navigation drawer - mega menu style
   Updated 2026-08-21.

   Markup: snippets/hanlyn-mobile-drawer.liquid, rendered from
   sections/header.liquid so it can read section.blocks. Two relocations on
   init:

     1. The drawer root moves to <body>. sticky-header carries a z-index and
        would otherwise trap the fixed-position drawer beneath the page.

     2. The hamburger trigger moves into .header__hamburger - the empty div the
        theme already ships as the first child of .header, already styled in
        custom.css with order:1 / z-index:10 / align-self:center for a Dawn
        `header-drawer` that header.liquid no longer renders. Filling it puts
        the button left of the logo using the theme's own layout, and stops
        base.css's `div:empty { display: none }` from collapsing the div.
        Falls back to .header__icons if that div is ever removed.

   Both re-run on shopify:section:load so the theme editor cannot leave
   orphaned or duplicated drawers behind.
   ========================================================================== */
(function () {
  'use strict';

  var TRANSITION_MS = 280;
  var instance = null;

  function portalDrawer() {
    var drawers = document.querySelectorAll('[data-hanlyn-drawer]');
    if (!drawers.length) return null;

    var keep = drawers[drawers.length - 1];
    for (var i = 0; i < drawers.length; i++) {
      if (drawers[i] !== keep && drawers[i].parentElement === document.body) {
        drawers[i].parentElement.removeChild(drawers[i]);
      }
    }

    if (keep.parentElement !== document.body) {
      document.body.appendChild(keep);
    }
    return keep;
  }

  function placeTrigger() {
    var triggers = document.querySelectorAll('.hanlyn-mobile-nav__trigger');
    if (!triggers.length) return;

    var host = document.querySelector('.header__hamburger') || document.querySelector('.header__icons');
    if (!host) return;

    var keep = triggers[triggers.length - 1];
    for (var i = 0; i < triggers.length; i++) {
      if (triggers[i] !== keep && triggers[i].parentElement) {
        triggers[i].parentElement.removeChild(triggers[i]);
      }
    }

    if (keep.parentElement !== host) {
      host.insertBefore(keep, host.firstChild);
    }
  }

  function Drawer(root) {
    this.root = root;
    this.closeTimer = null;
    this.lastFocus = null;
    this.tabs = root.querySelector('[data-hanlyn-drawer-tabs]');
    this.bindTabScroll();
  }

  Drawer.prototype.isOpen = function () {
    return this.root.classList.contains('is-open');
  };

  Drawer.prototype.open = function () {
    var self = this;
    if (this.isOpen()) return;

    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    this.lastFocus = document.activeElement;
    this.root.hidden = false;

    // Force a reflow so the transform transition runs from the closed state
    // rather than collapsing into the same frame as the attribute change.
    void this.root.offsetHeight;

    this.root.classList.add('is-open');
    document.body.classList.add('hanlyn-drawer-open');
    setTriggerState(true);
    this.updateArrows();

    window.setTimeout(function () {
      var target = self.root.querySelector('button[data-hanlyn-drawer-close]');
      if (target) target.focus();
      self.updateArrows();
    }, 60);
  };

  Drawer.prototype.close = function () {
    var self = this;
    if (!this.isOpen()) return;

    this.root.classList.remove('is-open');
    document.body.classList.remove('hanlyn-drawer-open');
    setTriggerState(false);

    this.closeTimer = window.setTimeout(function () {
      self.root.hidden = true;
      self.closeTimer = null;
    }, TRANSITION_MS);

    if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
      this.lastFocus.focus();
    }
  };

  Drawer.prototype.selectTab = function (index) {
    var tabs = this.root.querySelectorAll('[data-hanlyn-drawer-tab]');
    var panels = this.root.querySelectorAll('[data-hanlyn-drawer-tabpanel]');
    var i;

    for (i = 0; i < tabs.length; i++) {
      var isActive = tabs[i].getAttribute('data-hanlyn-drawer-tab') === index;
      tabs[i].classList.toggle('is-active', isActive);
      tabs[i].setAttribute('aria-selected', isActive ? 'true' : 'false');
      if (isActive && typeof tabs[i].scrollIntoView === 'function') {
        tabs[i].scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      }
    }

    for (i = 0; i < panels.length; i++) {
      panels[i].hidden = panels[i].getAttribute('data-hanlyn-drawer-tabpanel') !== index;
    }

    var body = this.root.querySelector('.hanlyn-mobile-drawer__body');
    if (body) body.scrollTop = 0;

    this.updateArrows();
  };

  Drawer.prototype.scrollTabs = function (direction) {
    if (!this.tabs) return;
    var amount = Math.max(120, Math.round(this.tabs.clientWidth * 0.7));
    this.tabs.scrollLeft += direction === 'prev' ? -amount : amount;
    // scroll-behavior: smooth means scrollLeft settles a frame or two later.
    var self = this;
    window.setTimeout(function () {
      self.updateArrows();
    }, 350);
  };

  Drawer.prototype.updateArrows = function () {
    if (!this.tabs) return;
    var prev = this.root.querySelector('[data-hanlyn-drawer-scroll="prev"]');
    var next = this.root.querySelector('[data-hanlyn-drawer-scroll="next"]');
    var max = this.tabs.scrollWidth - this.tabs.clientWidth;
    var x = this.tabs.scrollLeft;

    if (prev) prev.disabled = x <= 1;
    if (next) next.disabled = x >= max - 1;
  };

  Drawer.prototype.bindTabScroll = function () {
    var self = this;
    if (!this.tabs) return;
    this.tabs.addEventListener('scroll', function () {
      self.updateArrows();
    });
    window.addEventListener('resize', function () {
      self.updateArrows();
    });
    this.updateArrows();
  };

  function setTriggerState(expanded) {
    var trigger = document.querySelector('[data-hanlyn-drawer-open]');
    if (trigger) trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  // Delegated once at document level so a header re-render never leaves stale
  // listeners behind.
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    if (target.closest('[data-hanlyn-drawer-open]')) {
      event.preventDefault();
      if (instance) instance.open();
      return;
    }

    if (!instance) return;

    var arrow = target.closest('[data-hanlyn-drawer-scroll]');
    if (arrow && instance.root.contains(arrow)) {
      event.preventDefault();
      instance.scrollTabs(arrow.getAttribute('data-hanlyn-drawer-scroll'));
      return;
    }

    var tab = target.closest('[data-hanlyn-drawer-tab]');
    if (tab && instance.root.contains(tab)) {
      event.preventDefault();
      instance.selectTab(tab.getAttribute('data-hanlyn-drawer-tab'));
      return;
    }

    // Search: close the drawer, then open the header's search modal. Going to
    // /search directly rendered a broken page, and the modal is what the rest
    // of the site uses. The delay lets the close transition finish first.
    var searchBtn = target.closest('[data-hanlyn-drawer-search]');
    if (searchBtn && instance.root.contains(searchBtn)) {
      event.preventDefault();
      instance.close();
      window.setTimeout(function () {
        var summary = document.querySelector('.header__search summary, details-modal.header__search summary');
        if (summary) summary.click();
        var input = document.querySelector('.header__search input[type="search"], .header__search .search__input');
        if (input) input.focus();
      }, TRANSITION_MS + 60);
      return;
    }

    // Close button / overlay. Checked after the tab handlers so a click on a
    // control inside the panel is never mistaken for a dismissal.
    if (target.closest('[data-hanlyn-drawer-close]')) {
      event.preventDefault();
      instance.close();
      return;
    }

    var link = target.closest('.hanlyn-mobile-drawer__panel a[href]');
    if (link) instance.close();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    if (instance && instance.isOpen()) instance.close();
  });

  window.addEventListener('resize', function () {
    if (instance && instance.isOpen() && window.innerWidth >= 990) instance.close();
  });

  function init() {
    var root = portalDrawer();
    placeTrigger();
    if (!root) return;
    instance = new Drawer(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
